import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Product, Category, Unit, ProductUpdateRequest } from '../../types/product';
import { updateProduct, createProduct } from '../../api/product';
import { useNotifications } from '../../hooks/useNotifications';
import { t } from '../../utils/translations';
import { styles } from './ProductEditPanel.styles';

interface ProductEditPanelProps {
  open: boolean;
  product: Product | null;
  categories: Category[];
  units: Unit[];
  onClose: () => void;
  onUpdate: () => void;
}

const ProductEditPanel: React.FC<ProductEditPanelProps> = ({
  open,
  product,
  categories,
  units,
  onClose,
  onUpdate
}) => {
  const [formData, setFormData] = useState<ProductUpdateRequest>({
    name: '',
    unit: '',
    category: '',
  });
  const [saving, setSaving] = useState(false);

  const { showSuccess, showError } = useNotifications();

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        unit: product.unit,
        category: product.category,
      });
    } else {
      setFormData({
        name: '',
        unit: '',
        category: '',
      });
    }
  }, [product, open]);

  const handleChange = (field: keyof ProductUpdateRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      showError('Nazwa produktu jest wymagana', 'Błąd walidacji');
      return;
    }

    if (!formData.unit) {
      showError('Jednostka jest wymagana', 'Błąd walidacji');
      return;
    }

    try {
      setSaving(true);
      if (product) {
        await updateProduct(product.id, formData);
      } else {
        await createProduct({
          name: formData.name!,
          unit: formData.unit!,
          category: formData.category || null,
        });
        showSuccess('Produkt został dodany', 'Sukces');
      }
      onUpdate();
    } catch (error) {
      showError(error instanceof Error ? error.message : `Nie udało się ${product ? 'zaktualizować' : 'dodać'} produktu`, 'Błąd');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      onClose();
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open, onClose]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={styles.drawer}
      PaperProps={{ sx: styles.drawerPaper }}
    >
      <Box sx={styles.header} mt={7}>
        <Typography variant="h6">{product ? t('products.editProduct') : t('products.addProduct')}</Typography>
        <IconButton onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={styles.content}>
        <TextField
          label={t('products.productName')}
          value={formData.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          fullWidth
          required
          disabled={saving}
        />

            <TextField
              select
              label={t('products.unit')}
              value={formData.unit || ''}
              onChange={(e) => handleChange('unit', e.target.value)}
              fullWidth
              required
              disabled={saving}
            >
              {[...units]
                .sort((a, b) =>
                  t(`units.${a.key}`).localeCompare(t(`units.${b.key}`), 'pl')
                )
                .map((unit) => (
                  <MenuItem key={unit.key} value={unit.key}>
                    {t(`units.${unit.key}`)}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              select
              label={t('products.category')}
              value={formData.category || ''}
              onChange={(e) => handleChange('category', e.target.value)}
              fullWidth
              disabled={saving}
            >
              {[...categories]
                .sort((a, b) =>
                  t(`categories.${a.key}`).localeCompare(t(`categories.${b.key}`), 'pl')
                )
                .map((category) => (
                  <MenuItem key={category.key} value={category.key}>
                    {t(`categories.${category.key}`)}
                  </MenuItem>
                ))}
            </TextField>

        <Box sx={styles.actions}>
          <Button onClick={handleCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProductEditPanel;